import { useState, useEffect } from "react";
import classNames from "classnames";

import { useChildrenArray } from "../../lib/helpers/hooks/useChildrenArray";
import { useAscContext } from "../../context";
import { AscDropdownOptionProps } from "../AscDropdown/AscDropdownOptions/AscDropdownOptions";
import AscSidebarItem, {
  AscSidebarItemProps,
} from "./AscSidebarItem/AscSidebarItem";
import { ArrowDownIcon, ArrowUpCollapseIcon } from '../../components/AscIcons/index';

import styles from "./AscAdminSidebar.module.scss";

export interface AscSidebarProps {
  className?: string;
  children: JSX.Element[];
  collapsed?: boolean;
  width?: string;
  collapsedWidth?: string;
  header?: React.ReactNode;
  collapsedHeader?: React.ReactNode;
  headerClassName?: string;
  onClose?: () => void;
  selectedId?: string | null;
  onSelectionChange?: (activeItem: AscSidebarItemProps) => void;
  customNavigateFunction?: (path: string) => void;
  mobileOptions?: {
    name: string;
    avatarUrl: string;
    globalRank?: number;
    classRank?: number;
    points?: number;
    coinsCount?: number;
    userDropdownOptions?: AscDropdownOptionProps[];
    onLogout?: () => void;
  };
}

function AscAdminSidebar({
  className,
  children,
  collapsed = false,
  width = "15.25rem",
  collapsedWidth = "4.75rem",
  header,
  collapsedHeader,
  headerClassName,
  onClose,
  selectedId,
  onSelectionChange,
  mobileOptions,
  customNavigateFunction,
}: AscSidebarProps) {
  const sidebarItems = useChildrenArray<AscSidebarItemProps>(children);
  const [activeId, setActiveId] = useState<string | any>(selectedId);
  const [dropdownOpen, setDropdownState] = useState<Boolean>(false);
  const { navigateFunction } = useAscContext();

  function navigateTo(path: string) {
    customNavigateFunction
      ? customNavigateFunction(path)
      : navigateFunction(path);
  }

  function handleActiveChange(activeItem: AscSidebarItemProps) {
    if (activeItem?.disabled) {
      return;
    }
    if (activeItem?.type === "function") {
      activeItem?.onClick?.();
    } else {
      navigateTo(activeItem?.path);
    }
    setActiveId(activeItem?.id);

    onSelectionChange?.(activeItem);
  }

  useEffect(() => {
    if (selectedId === undefined && sidebarItems?.length) {
      setActiveId(sidebarItems[0]?.id);
    }
  }, []);

  return (
    <aside
      data-testid="asc-sidebar"
      className={classNames(
        styles.ground,
        {
          [styles.collapsed]: collapsed,
        },
        className
      )}
      style={{ width: collapsed ? collapsedWidth : width }}
    >
      {header ? (
        <header
          className={classNames(
            styles.header,
            {
              [styles.collapsedHeader]: !collapsedHeader && collapsed,
            },
            headerClassName
          )}
        >
          {header}
        </header>
      ) : null}
      {collapsedHeader ? (
        <header className={styles.collapsedHeader}>{collapsedHeader}</header>
      ) : null}
      <ul className={styles.sideBarItems}>
        {sidebarItems.map((sidebarItem) => {
          return sidebarItem.parent
            ? dropdownOpen && <li 
                key={sidebarItem?.id}
                onClick={() => {
                  handleActiveChange(sidebarItem);
                }}
                className={classNames(styles.sidebarItem, {
                  [styles.disabled]: sidebarItem?.disabled,
                  [styles.active]: activeId === sidebarItem?.id,
                })}
              >
                {sidebarItem?.id === activeId ? (
                  <span className={styles.icon}>{sidebarItem?.icon}</span>
                ) : <span className={styles.icon}/>}
                <div className={styles.content}>{sidebarItem?.children}</div>

              </li>
            :(
              <li 
                key={sidebarItem?.id}
                onClick={() => {
                  sidebarItem.isParent
                    ? setDropdownState(!dropdownOpen)
                    : handleActiveChange(sidebarItem);
                }}
                className={classNames(styles.sidebarItem, {
                  [styles.disabled]: sidebarItem?.disabled,
                  [styles.active]: activeId === sidebarItem?.id,
                })}
              >
                {sidebarItem?.icon ? (
                  <span className={styles.icon}>{sidebarItem?.icon}</span>
                ) : null}
                <div className={styles.content}>{sidebarItem?.children}</div>
                {sidebarItem?.isParent &&
                  (dropdownOpen ? <ArrowUpCollapseIcon className={styles.icon}/> : <ArrowDownIcon className={styles.icon}/>)
                }
              </li>
            )
          }
        )}
      </ul>
      <div className={styles.backdrop} onClick={onClose} />
    </aside>
  );
}

AscAdminSidebar.Item = AscSidebarItem;

export default AscAdminSidebar;
